import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

const toAuthUser = (session: Session | null): AuthUser | null => {
  const user = session?.user;
  if (!user) return null;

  const meta = user.user_metadata as Record<string, string | undefined>;
  return {
    uid: user.id,
    email: user.email ?? null,
    displayName: meta.full_name || meta.name || null,
    photoURL: meta.avatar_url || meta.picture || null,
  };
};

// Module-level cache so non-React code (sync engine, repositories) can read
// the signed-in user synchronously, like firebase's auth.currentUser did.
let currentUser: AuthUser | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  currentUser = toAuthUser(session);
});

export const getCurrentUser = (): AuthUser | null => currentUser;

export class AuthService {
  async signIn(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  }

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(toAuthUser(session));
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }
}
